import { useEffect, useState } from "react";

/**
 * Tracks how many pixels the on-screen keyboard (or any other visual-viewport
 * shrink) overlaps the bottom of the layout viewport in a mobile web browser.
 *
 * Bottom-anchored fixed elements (e.g. a bottom drawer) are positioned against
 * the *layout* viewport, which does not shrink when the virtual keyboard opens
 * — so the keyboard slides up over them. The `window.visualViewport` API
 * reports the actually-visible region; the overlap is everything below it:
 *
 *   inset = layoutHeight - (visualViewport.height + visualViewport.offsetTop)
 *
 * Apply the returned value as a bottom offset / padding so the element floats
 * above the keyboard. Returns 0 when there is no keyboard, on browsers without
 * `visualViewport`, and inside the native app (where Capacitor resizes the
 * webview itself — see `useVisualViewportHeight` for that path).
 */
export function useKeyboardInset(): number {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const update = () => {
      const overlap =
        window.innerHeight - (vv.height + vv.offsetTop);
      // Clamp: sub-pixel rounding can produce tiny negatives, and we never
      // want to push the element down past its anchor.
      setInset(Math.max(0, Math.round(overlap)));
    };

    update();
    vv.addEventListener("resize", update);
    vv.addEventListener("scroll", update);
    return () => {
      vv.removeEventListener("resize", update);
      vv.removeEventListener("scroll", update);
    };
  }, []);

  return inset;
}
